import React from 'react';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';
import { Formik, Form, Field, ErrorMessage } from 'formik';

import { asyncActions, actionsGenerator } from '@docavenue/core';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Stack,
} from '@mui/material';
import { useTranslation } from '@/src/i18n';
import TextFieldFormik from '../../molecules/Input/TextFieldFormik';

import { enqueueSnackbar } from '@/src/actions/snackbar';
import usePromise from '../Dialogs/usePromise';
import Dialog from '@/components/molecules/Dialog/Dialog';

type Props = {
  innerRef: React.Ref<unknown>;
};

const validationSchema = yup.object().shape({
  name: yup.string().required('error:error_lastname_required'),
  firstName: yup.string().required('error:error_firstname_required'),
  mail: yup
    .string()
    .email('error:invalide_format_email')
    .required('error:required_email_error'),
  message: yup.string().required('error:error_message_empty_form_pro'),
});

const contactActions = actionsGenerator({
  resource: 'contacts',
  chunkUrlResource: 'contacts/support',
  scope: 'pat-public',
});

const ContactFormDialog = ({ innerRef }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isOpen, onConfirm, onCancel } = usePromise(innerRef);

  const onSubmit = React.useCallback(async (values, { setSubmitting }) => {
    await asyncActions(dispatch, contactActions.create(values));
    setSubmitting(false);
    onConfirm(null);
    dispatch(
      enqueueSnackbar({
        message: t('ok-submit-message'),
        duration: 5000,
        severity: 'success',
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'center',
        },
      }),
    );
  }, []);

  return (
    <Dialog open={isOpen} title={t('modify_infos')} onClose={onCancel}>
      <Formik
        validationSchema={validationSchema}
        initialValues={{
          name: '',
          firstName: '',
          mail: '',
          message: '',
          mailTo: 'pro',
        }}
        onSubmit={onSubmit}
      >
        {({ isValid, isSubmitting, dirty }) => (
          <Form method="POST">
            <DialogContent>
              <Box sx={{ minWidth: 450 }}>
                <Stack spacing={2}>
                  <Field
                    id="last_name"
                    type="text"
                    name="name"
                    placeholder={t('form-last-name')}
                    component={TextFieldFormik}
                  />
                  <Field
                    id="first_name"
                    type="text"
                    name="firstName"
                    placeholder={t('form-first-name')}
                    component={TextFieldFormik}
                  />
                  <Field
                    id="mail"
                    type="email"
                    name="mail"
                    placeholder={t('form-mail')}
                    required
                    component={TextFieldFormik}
                  />
                  <Field
                    id="phone"
                    type="text"
                    name="phoneNumber"
                    className="mobile is-optionnal"
                    placeholder={t('form-phone-number')}
                    component={TextFieldFormik}
                  />
                  <Field id="message" name="message" component="textarea" />
                  <p className="input-error">
                    <ErrorMessage name="message">
                      {error => t(error)}
                    </ErrorMessage>
                  </p>
                </Stack>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                type="submit"
                color="secondary"
                variant="contained"
                disabled={!dirty || !isValid || isSubmitting}
              >
                {t('form-button-send')}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default React.forwardRef<unknown, Omit<Props, 'innerRef'>>(
  (props, ref) => <ContactFormDialog {...props} innerRef={ref} />,
);
